import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { Theme } from '@mui/material/styles';
import { Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

export interface Column<T> {
  key: string;
  header: string;
  render(row: T): ReactNode;
  /** Makes the column sortable. */
  sortValue?(row: T): string | number | null;
  align?: 'left' | 'right';
  /** Hide on narrow screens: 'sm' = phones, 'md' = tablets (phones show every column, stacked). */
  hideBelow?: 'sm' | 'md';
  width?: number | string;
  /** A long heading on two lines, broken where its header has a newline, so the column is narrower. */
  wrapHeader?: boolean;
}

export interface DataTableProps<T> {
  rows: T[] | undefined;
  columns: Column<T>[];
  rowKey(row: T): string;
  /** Free-text search over what this returns for each row. */
  searchText?(row: T): string;
  searchPlaceholder?: string;
  /** Filters or toggles shown next to the search box. */
  toolbar?: ReactNode;
  initialSort?: { key: string; direction: 'asc' | 'desc' };
  onRowClick?(row: T): void;
  /** Buttons or a menu at the end of each row. */
  rowActions?(row: T): ReactNode;
  loading?: boolean;
  error?: Error | null;
  onRetry?(): void;
  /** Shown when there are no rows at all (not when a search finds nothing). */
  empty: ReactNode;
  pageSize?: number;
  /** Accessible name for the table. */
  label: string;
  /**
   * Tick boxes for choosing rows (by rowKey), e.g. to delete several. The box in the heading
   * ticks every row the search finds, on all pages.
   */
  selection?: { selected: ReadonlySet<string>; onChange(next: Set<string>): void };
  /** Told the rows the search finds (all pages), e.g. for acting on what the list shows. */
  onShown?(rows: T[]): void;
  /**
   * For long lists paged by the server (sessions): rows are one page already, sorting and
   * paging ask the server, and searching is done by the page's own filters.
   */
  server?: {
    total: number;
    page: number; // 1-based
    pageSize: number;
    onPage(page: number): void;
    sort: { key: string; direction: 'asc' | 'desc' };
    onSort(sort: { key: string; direction: 'asc' | 'desc' }): void;
  };
}

/**
 * The standard table: a hairline panel with 16 px corners, sortable headers, optional
 * search, and Previous/Next paging. Sorting, searching and paging happen in the browser,
 * which suits lists of up to a few thousand rows; long lists (sessions) page on the server.
 */
export function DataTable<T>(p: DataTableProps<T>) {
  const phone = useMediaQuery((t: Theme) => t.breakpoints.down('sm'));
  const [query, setQuery] = useState('');
  const [localSort, setSort] = useState(p.initialSort ?? null);
  const sort = p.server ? p.server.sort : localSort;
  const [page, setPage] = useState(0);
  const pageSize = p.pageSize ?? 25;

  const shown = useMemo(() => {
    let rows = p.rows ?? [];
    if (p.server) return rows;
    const q = query.trim().toLowerCase();
    if (q && p.searchText) rows = rows.filter((r) => p.searchText!(r).toLowerCase().includes(q));
    const col = sort && p.columns.find((c) => c.key === sort.key);
    if (col?.sortValue) {
      const dir = sort!.direction === 'asc' ? 1 : -1;
      rows = [...rows].sort((a, b) => {
        const x = col.sortValue!(a);
        const y = col.sortValue!(b);
        if (x === y) return 0;
        if (x === null) return 1; // blanks last either way
        if (y === null) return -1;
        return (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), 'en-AU', { numeric: true })) * dir;
      });
    }
    return rows;
  }, [p.rows, p.columns, p.searchText, query, sort, p.server]);

  // Told only when the rows shown change, not on every new array: a page that keeps them in
  // state and passes new columns each render would otherwise re-render forever.
  const { onShown } = p;
  const told = useRef<T[] | null>(null);
  useEffect(() => {
    const before = told.current;
    if (before && before.length === shown.length && before.every((r, i) => r === shown[i])) return;
    told.current = shown;
    onShown?.(shown);
  }, [onShown, shown]);

  const total = p.server ? p.server.total : shown.length;
  const size = p.server ? p.server.pageSize : pageSize;
  const pages = Math.max(1, Math.ceil(total / size));
  const current = p.server ? p.server.page - 1 : Math.min(page, pages - 1);
  const pageRows = p.server ? shown : shown.slice(current * pageSize, current * pageSize + pageSize);
  const goTo = (n: number) => (p.server ? p.server.onPage(n + 1) : setPage(n));
  const cols = p.columns.length + (p.rowActions ? 1 : 0) + (p.selection ? 1 : 0);
  const sel = p.selection;
  const shownKeys = sel ? shown.map(p.rowKey) : [];
  const tickedShown = sel ? shownKeys.filter((k) => sel.selected.has(k)).length : 0;
  const tickAll = () => {
    if (!sel) return;
    const next = new Set(sel.selected);
    if (tickedShown === shownKeys.length) shownKeys.forEach((k) => next.delete(k));
    else shownKeys.forEach((k) => next.add(k));
    sel.onChange(next);
  };
  const tickBox = (row: T) => {
    const k = p.rowKey(row);
    return (
      <Checkbox
        size="small"
        checked={sel!.selected.has(k)}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          const next = new Set(sel!.selected);
          if (e.target.checked) next.add(k);
          else next.delete(k);
          sel!.onChange(next);
        }}
        slotProps={{ input: { 'aria-label': 'Choose this row' } }}
        sx={{ m: -1 }}
      />
    );
  };
  const hide = (c: { hideBelow?: 'sm' | 'md' }) => (c.hideBelow ? { display: { xs: 'none', [c.hideBelow]: 'table-cell' } } : {});

  const toggleSort = (key: string) => {
    const next = sort?.key === key ? { key, direction: sort.direction === 'asc' ? ('desc' as const) : ('asc' as const) } : { key, direction: 'asc' as const };
    if (p.server) return p.server.onSort(next);
    setSort(next);
    setPage(0);
  };

  return (
    <Box>
      {(p.searchText || p.toolbar) && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2, alignItems: { sm: 'center' } }}>
          {p.searchText && (
            <TextField
              size="small"
              placeholder={p.searchPlaceholder ?? 'Search'}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(0);
              }}
              sx={{ width: { xs: '100%', sm: 320 } }}
              slotProps={{
                htmlInput: { 'aria-label': p.searchPlaceholder ?? 'Search' },
                input: { startAdornment: <InputAdornment position="start"><Search size={18} strokeWidth={1.5} /></InputAdornment> },
              }}
            />
          )}
          {p.toolbar}
        </Stack>
      )}

      {p.error ? (
        <Alert severity="error" action={p.onRetry && <Button size="small" onClick={p.onRetry}>Try again</Button>}>
          {p.error.message}
        </Alert>
      ) : !p.loading && (p.server ? p.server.total : (p.rows?.length ?? 0)) === 0 ? (
        <Paper variant="outlined" sx={{ p: { xs: 3, md: 5 } }}>
          {p.empty}
        </Paper>
      ) : phone ? (
        // On phones each row is a small card: the first column as the title, the rest labelled.
        <Paper variant="outlined" component="ul" aria-label={p.label} sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {p.loading
            ? Array.from({ length: 3 }, (_, i) => (
                <Box component="li" key={i} sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" />
                </Box>
              ))
            : pageRows.map((row) => {
                const [first, ...rest] = p.columns;
                return (
                  <Box
                    component="li"
                    key={p.rowKey(row)}
                    onClick={p.onRowClick ? () => p.onRowClick!(row) : undefined}
                    sx={{ p: 2, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 }, cursor: p.onRowClick ? 'pointer' : undefined }}
                  >
                    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      {sel && <Box sx={{ pt: 0.5, pl: 1 }}>{tickBox(row)}</Box>}
                      <Box sx={{ minWidth: 0, flex: sel ? 1 : undefined }}>{first!.render(row)}</Box>
                      {p.rowActions && <Box onClick={(e) => e.stopPropagation()} sx={{ mt: -0.5, mr: -1 }}>{p.rowActions(row)}</Box>}
                    </Stack>
                    <Box component="dl" sx={{ m: 0, mt: 1, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, rowGap: 0.5 }}>
                      {rest.map((c) => (
                        <Box key={c.key} sx={{ display: 'contents' }}>
                          <Typography component="dt" variant="body2" color="textSecondary">{c.header.replace(/\n/g, ' ')}</Typography>
                          <Box component="dd" sx={{ m: 0, minWidth: 0 }}>{c.render(row)}</Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })}
          {!p.loading && shown.length === 0 && (
            <Box component="li" sx={{ p: 2, color: 'text.secondary' }}>Nothing matches “{query}”.</Box>
          )}
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ overflowX: 'auto', position: 'relative' }}>
          <Table aria-label={p.label} aria-busy={p.loading || undefined}>
            <TableHead>
              <TableRow>
                {sel && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={shownKeys.length > 0 && tickedShown === shownKeys.length}
                      indeterminate={tickedShown > 0 && tickedShown < shownKeys.length}
                      onChange={tickAll}
                      disabled={!shownKeys.length}
                      slotProps={{ input: { 'aria-label': 'Choose all rows' } }}
                    />
                  </TableCell>
                )}
                {p.columns.map((c) => (
                  <TableCell key={c.key} align={c.align} sx={{ width: c.wrapHeader ? (c.width ?? '1%') : c.width, whiteSpace: c.wrapHeader ? 'pre' : undefined, ...hide(c) }} sortDirection={sort?.key === c.key ? sort.direction : false}>
                    {c.sortValue ? (
                      <TableSortLabel active={sort?.key === c.key} direction={sort?.key === c.key ? sort.direction : 'asc'} onClick={() => toggleSort(c.key)}>
                        {c.header}
                      </TableSortLabel>
                    ) : (
                      c.header
                    )}
                  </TableCell>
                ))}
                {p.rowActions && <TableCell align="right" sx={{ width: 56, position: 'relative' }}><Box component="span" sx={visuallyHidden}>Actions</Box></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {p.loading
                ? Array.from({ length: 4 }, (_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: cols }, (_, j) => (
                        <TableCell key={j}>
                          <Skeleton variant="text" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : pageRows.map((row) => (
                    <TableRow
                      key={p.rowKey(row)}
                      hover={!!p.onRowClick}
                      onClick={p.onRowClick ? () => p.onRowClick!(row) : undefined}
                      selected={!!sel?.selected.has(p.rowKey(row))}
                      sx={{ cursor: p.onRowClick ? 'pointer' : undefined, '&:last-child td': { borderBottom: 0 } }}
                    >
                      {sel && <TableCell padding="checkbox">{tickBox(row)}</TableCell>}
                      {p.columns.map((c) => (
                        <TableCell key={c.key} align={c.align} sx={hide(c)}>
                          {c.render(row)}
                        </TableCell>
                      ))}
                      {p.rowActions && (
                        <TableCell align="right" onClick={(e) => e.stopPropagation()} sx={{ py: 0.5 }}>
                          {p.rowActions(row)}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
              {!p.loading && shown.length === 0 && (
                <TableRow>
                  <TableCell colSpan={cols} sx={{ color: 'text.secondary', py: 4, borderBottom: 0 }}>
                    Nothing matches “{query}”.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {!p.loading && !p.error && total > 0 && (
        <Stack direction="row" sx={{ mt: 2, alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="body2" color="textSecondary" className="num">
            {total <= size ? `${total} ${total === 1 ? 'row' : 'rows'}` : `Showing ${(current * size + 1).toLocaleString('en-AU')} to ${Math.min(total, (current + 1) * size).toLocaleString('en-AU')} of ${total.toLocaleString('en-AU')}`}
          </Typography>
          {pages > 1 && (
            <Stack direction="row" spacing={1.5}>
              <Button size="small" disabled={current === 0} onClick={() => goTo(current - 1)}>Previous</Button>
              <Button size="small" disabled={current >= pages - 1} onClick={() => goTo(current + 1)}>Next</Button>
            </Stack>
          )}
        </Stack>
      )}
    </Box>
  );
}

// Note: in sx, width: 1 means 100%, so pixel sizes are written as strings.
const visuallyHidden = { position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', margin: '-1px' } as const;
